import {
	getSession,
} from 'Vulture/Session.js';
import {
	broncoURL,
} from 'Vulture/ENV.js';
import {
	getRawPermissions,
} from 'Vulture/Permissions.js';
import BroncoSDK from 'Vulture/lib/BroncoSDK.js';
import { sparkieEnabled } from 'Vulture/lib/miaw/flags.js';
// Side-effect import: caps the widget's z-index below modals and defines the
// body-class rule used by hideSalesforceChat/showSalesforceChat.
import 'Vulture/lib/SalesforceChat.module.css';

const broncoSDK = new BroncoSDK({
	baseURL: broncoURL,
});

let initialized = false;
// Track the injected script tag and the ready listener so clearSalesforceChat()
// can tear them down — otherwise a logout/login cycle in the same SPA session
// would stack duplicate scripts and listeners.
let bootstrapScript = null;
let readyHandler = null;

const SALESFORCE_ORG_ID = '00Dj0000001rkAe';
const SALESFORCE_DEPLOYMENT_NAME = 'Agentforce_Service_Deployment';
const SALESFORCE_SITE_URL = 'https://touchsource.my.site.com/ESWAgentforceServiceDep1736448239773';
const SALESFORCE_SCRT2_URL = 'https://touchsource.my.salesforce-scrt.com';
const SALESFORCE_BOOTSTRAP_URL = `${SALESFORCE_SITE_URL}/assets/js/bootstrap.min.js`;

// Resolve the logged-in user's first/last name. The name lives on the crow_auth
// user record (the bronco app `user` table only has uuid + display_name) and is
// returned by the self-scoped permissions endpoint. That response is already
// fetched at login and cached in Permissions.js, so prefer the cached copy and
// only fall back to a fetch if the chat becomes ready before permissions have
// loaded. Returns empty strings on any failure so pre-chat still sends the email.
const fetchUserName = async (session) => {
	const empty = {
		firstName: '',
		lastName: '',
	};

	let data = getRawPermissions();

	if (!data) {
		try {
			broncoSDK.setAuth(session.session, session.user);
			data = await broncoSDK.getUserPermissions();
		} catch (err) {
			console.error('Error fetching user name for Salesforce pre-chat:', err);
			return empty;
		}
	}

	return {
		firstName: data.first_name || '',
		lastName: data.last_name || '',
	};
};

const setPrechatFields = async () => {
	const session = getSession();
	if (!session) {
		return;
	}

	// The bootstrap global is injected by the Salesforce script at runtime; bail
	// if it isn't present so a load failure doesn't throw.
	if (typeof window.embeddedservice_bootstrap === 'undefined') {
		return;
	}

	// Email is on the session cookie; the name is resolved separately (see
	// fetchUserName) because it lives on the crow_auth record, not the bronco
	// app `user` table.
	const { firstName, lastName } = await fetchUserName(session);

	const hiddenFields = {
		'customerFirstName': firstName,
		'customerLastName': lastName,
		'customerEmail': session.email || '',
	};

	try {
		// Agentforce (WebV2) messaging only implements setHiddenPrechatFields —
		// setVisiblePrechatFields does not exist on this client, so visible fields
		// cannot be prefilled. Keys are the Channel Variable Names from Messaging
		// Settings → Parameter Mappings; each must be configured as a HIDDEN
		// pre-chat field on the deployment or it's rejected as an invalid name.
		window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(hiddenFields);
	} catch (err) {
		console.error('Error setting Salesforce pre-chat fields:', err);
	}
};

const initEmbeddedMessaging = () => {
	if (typeof window.embeddedservice_bootstrap === 'undefined') {
		console.error('Salesforce bootstrap script loaded but embeddedservice_bootstrap is undefined');
		return;
	}

	try {
		window.embeddedservice_bootstrap.settings.language = 'en_US';
		// Lift the chat button above the dashboard pagination/action bars it was
		// overlapping (SF default is 25px from the bottom). Format is 'bottom, right'.
		window.embeddedservice_bootstrap.settings.chatButtonPosition = '90px, 30px';
		window.embeddedservice_bootstrap.init(
			SALESFORCE_ORG_ID,
			SALESFORCE_DEPLOYMENT_NAME,
			SALESFORCE_SITE_URL,
			{
				scrt2URL: SALESFORCE_SCRT2_URL,
			}
		);
	} catch (err) {
		console.error('Error loading Embedded Messaging:', err);
	}
};

export const initSalesforceChat = () => {
	if (initialized) {
		return;
	}

	// Sparkie replaces the SF floating widget for enabled (internal) users — don't
	// initialize a second chat entry point for them.
	if (sparkieEnabled()) {
		return;
	}

	if (!getSession()) {
		return;
	}

	initialized = true;

	// If the bootstrap already loaded earlier in this SPA session, the SDK keeps
	// its global and rendered widget alive across logout/login — re-injecting the
	// script or calling init() a second time is unsupported and would stack a
	// duplicate widget. The onEmbeddedMessagingReady event has already fired, so
	// just refresh the pre-chat identity for the newly logged-in user.
	if (typeof window.embeddedservice_bootstrap !== 'undefined') {
		setPrechatFields();
		return;
	}

	// Register event listeners BEFORE loading the bootstrap script to avoid race conditions
	readyHandler = () => {
		setPrechatFields();
	};
	window.addEventListener('onEmbeddedMessagingReady', readyHandler);

	// Load the Salesforce bootstrap script
	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.src = SALESFORCE_BOOTSTRAP_URL;
	script.onload = initEmbeddedMessaging;
	// If the script can't load (CDN blocked/offline), reset state so a later
	// call can retry instead of being permanently stuck "initialized".
	script.onerror = () => {
		console.error('Failed to load Salesforce bootstrap script:', SALESFORCE_BOOTSTRAP_URL);
		clearSalesforceChat();
	};
	bootstrapScript = script;
	document.head.appendChild(script);
};

// Hide/show the Salesforce widget without tearing down the SDK (init() can't
// be re-run). Used by the internal n8n design assistant (N8nChat) so only one
// chatbot occupies the bottom-right corner at a time: the Programming pages
// mount N8nChat and hide this one, then restore it on unmount. Implemented as
// a body class + CSS rule (see SalesforceChat.module.css) so it still applies
// when the SDK's async bootstrap finishes after the page mounted.
const HIDE_BODY_CLASS = 'ts-hide-salesforce-chat';

export const hideSalesforceChat = () => {
	document.body.classList.add(HIDE_BODY_CLASS);
};

export const showSalesforceChat = () => {
	document.body.classList.remove(HIDE_BODY_CLASS);
};

export const clearSalesforceChat = () => {
	if (!initialized) {
		return;
	}

	try {
		if (typeof window.embeddedservice_bootstrap !== 'undefined' &&
			window.embeddedservice_bootstrap.userVerificationAPI) {
			window.embeddedservice_bootstrap.userVerificationAPI.clearSession(true);
		}
	} catch (err) {
		console.error('Error clearing Salesforce chat session:', err);
	}

	// Tear down the listener and injected script so the next login starts clean
	// instead of stacking duplicates.
	if (readyHandler) {
		window.removeEventListener('onEmbeddedMessagingReady', readyHandler);
		readyHandler = null;
	}
	if (bootstrapScript && bootstrapScript.parentNode) {
		bootstrapScript.parentNode.removeChild(bootstrapScript);
	}
	bootstrapScript = null;

	initialized = false;
};
